import { useContext, useEffect, useRef, useState } from 'react';
import { CustomScrollbarV2, ModernTable, SoundNotificationContext, useNotificationsV2 } from '@uni-space/uni-components';
import './Bigs.scss';
import { AcceptanceBigHardwareHubResponse, BigsErrorRow, BigsSuccessRow } from '../model/bigs';
import { useBigsTableSettings } from '../model/useBigsTableSettings';
import { ensureStarted, getSortingCenterHub } from '@shared/config/signalR/SortingSettingsHub';
import { useTranslation } from 'react-i18next';
import { StatusAcceptanceEnum } from '@features/ConsolidatorTracking/model/consolidator';
import { KeycloakContext } from '@shared/config/context/KeycloakContext';

const getRowId = (trackNumber?: string | null) =>
  `${trackNumber ?? 'unknown'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// в ленте ошибок показываем только последние сканирования, счётчик при этом общий
const MAX_VISIBLE_ERRORS = 5;

const normalizeUserId = (userId?: string | null) => userId?.trim().toLowerCase() ?? '';

// событие хаба приходит всем подключённым клиентам, поэтому сверяем пользователя из payload с текущим
const isCurrentUserPayload = (payloadUserId?: string | null, currentUserId?: string | null) => {
  const currentId = normalizeUserId(currentUserId);
  return currentId !== '' && normalizeUserId(payloadUserId) === currentId;
};

const parseScanDate = (scanAt?: string | null) => (scanAt ? new Date(scanAt) : new Date());

const getStatusLabel = (status: StatusAcceptanceEnum, t: (key: string) => string) => {
  switch (status) {
    case StatusAcceptanceEnum.Accepted:
      return t('accepted');
    case StatusAcceptanceEnum.AcceptedNeedGroundTransport:
      return t('acceptedNeedGroundTransport');
    case StatusAcceptanceEnum.Already:
      return t('alreadyScanned');
    case StatusAcceptanceEnum.AlreadyAnotherWarehouse:
      return t('alreadyAnotherWarehouse');
    case StatusAcceptanceEnum.Canceled:
      return t('canceled');
    case StatusAcceptanceEnum.ServiceRestriction:
      return t('serviceRestriction');
    case StatusAcceptanceEnum.NotFound:
      return t('parcelNotFound');
    case StatusAcceptanceEnum.UserNotFound:
      return t('userNotFound');
    case StatusAcceptanceEnum.QualityControl:
      return t('qualityControl');
    case StatusAcceptanceEnum.HighDensity:
      return t('highDensity');
    case StatusAcceptanceEnum.InvalidDimensions:
      return t('invalidDimensions');
    default:
      return t('parcelNotFound');
  }
};

export default function Bigs() {
  const { t, i18n } = useTranslation();
  const { getErrorStatusView, successColumns } = useBigsTableSettings();
  const [successRows, setSuccessRows] = useState<BigsSuccessRow[]>([]);
  const [errorRows, setErrorRows] = useState<BigsErrorRow[]>([]);
  const { createNotification } = useNotificationsV2();
  const { userInfo } = useContext(KeycloakContext);
  // держим id в ref, чтобы не пересоздавать подписку на хаб при обновлении userInfo
  const currentUserIdRef = useRef<string | undefined>(userInfo?.sub);
  const {
    onSoundAlready,
    onSoundCanceled,
    onSoundError,
    onSoundNotAvailablePartner,
    onSoundNotFound,
    onSoundServiceRestriction,
    onSoundSuccess,
  } = useContext(SoundNotificationContext);

  const addSuccessRow = (payload: AcceptanceBigHardwareHubResponse) => {
    const trackNumber = payload.trackNumber ?? '—';
    const row: BigsSuccessRow = {
      id: getRowId(payload.trackNumber),
      trackNumber,
      regionZone: payload.regionZone ?? '—',
      bagCode: payload.bagCode ?? '—',
      sortingCode: payload.sortingCode ?? '—',
      weight: payload.weight?.value ?? null,
      length: payload.length ?? null,
      width: payload.width ?? null,
      height: payload.height ?? null,
      density: payload.density ?? null,
      recommendedBox: payload.recommendedBox ?? null,
      needGroundTransport: payload.status === StatusAcceptanceEnum.AcceptedNeedGroundTransport,
      scanAt: parseScanDate(payload.scanAt),
    };
    setSuccessRows((prev) => [row, ...prev]);
  };

  const addErrorRow = (payload: AcceptanceBigHardwareHubResponse, status: StatusAcceptanceEnum) => {
    const row: BigsErrorRow = {
      id: getRowId(payload.trackNumber),
      trackNumber: payload.trackNumber ?? '—',
      status,
    };
    setErrorRows((prev) => [row, ...prev]);
  };

  useEffect(() => {
    currentUserIdRef.current = userInfo?.sub;
  }, [userInfo?.sub]);

  useEffect(() => {
    const conn = getSortingCenterHub();

    const onAcceptanceDws = (payload: AcceptanceBigHardwareHubResponse) => {
      if (!payload) return;
      // показываем только сканирования текущего пользователя
      if (!isCurrentUserPayload(payload.userId, currentUserIdRef.current)) return;
      const { status } = payload;
      if (status === StatusAcceptanceEnum.Accepted) {
        addSuccessRow(payload);
        createNotification({
          type: 'success',
          message: { title: getStatusLabel(status, t) },
        });
        onSoundSuccess();
      } else if (status === StatusAcceptanceEnum.AcceptedNeedGroundTransport) {
        addSuccessRow(payload);
        createNotification({
          type: 'warning',
          message: { title: getStatusLabel(status, t) },
        });
        onSoundSuccess();
      } else {
        addErrorRow(payload, status);
        createNotification({
          type: 'error',
          message: { title: getStatusLabel(status, t) },
        });
        switch (status) {
          case StatusAcceptanceEnum.Already:
            onSoundAlready();
            break;
          case StatusAcceptanceEnum.Canceled:
            onSoundCanceled();
            break;
          case StatusAcceptanceEnum.NotFound:
            onSoundNotFound();
            break;
          case StatusAcceptanceEnum.ServiceRestriction:
            onSoundServiceRestriction();
            break;
          case StatusAcceptanceEnum.AlreadyAnotherWarehouse:
            onSoundNotAvailablePartner();
            break;
          default:
            onSoundError();
            break;
        }
      }
    };

    conn.on('AcceptanceDws', onAcceptanceDws);

    conn.onreconnecting((err) => console.warn('SignalR reconnecting…', err?.message));
    conn.onreconnected((newId) => console.info('SignalR reconnected', newId));
    conn.onclose((err) => console.error('SignalR closed', err?.message));

    (async () => {
      await ensureStarted(conn);
      console.info('SignalR connected', conn.connectionId);
    })();

    return () => {
      conn.off('AcceptanceDws', onAcceptanceDws);
    };
  }, []);

  const visibleErrorRows = errorRows.slice(0, MAX_VISIBLE_ERRORS);

  return (
    <CustomScrollbarV2 height={'calc(100vh - 100px)'}>
      <div className="ct6-page bigs-page">
        <div className="ct6-card ct6-card--danger">
          <div className="ct6-cardHead">
            <div className="bigs-errorsHeadLeft">
              <div className="ct6-title">{t('bigs.tables.errors.title')}</div>
              {errorRows.length > MAX_VISIBLE_ERRORS && (
                <div className="ct6-muted">{t('bigs.tables.errors.showingLast', { max: MAX_VISIBLE_ERRORS })}</div>
              )}
            </div>
            <div className="ct6-counter ct6-counterDanger">{errorRows.length}</div>
          </div>

          {visibleErrorRows.length === 0 ? (
            <div className="bigs-errorsEmpty">{t('bigs.tables.errors.empty')}</div>
          ) : (
            <div className="bigs-errorsList">
              {visibleErrorRows.map((row) => {
                const statusView = getErrorStatusView(row.status);
                return (
                  <div key={row.id} className="bigs-errorCard">
                    {statusView.component}
                    <div className="bigs-errorCardBody">
                      <div className="bigs-errorTrack">{row.trackNumber}</div>
                      <div className="bigs-errorStatus">{statusView.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="ct6-card">
          <div className="ct6-cardHead">
            <div className="ct6-title">{t('bigs.tables.success.title')}</div>
            <div className="ct6-counter">{successRows.length}</div>
          </div>

          <ModernTable<BigsSuccessRow>
            data={successRows}
            columns={successColumns}
            lang={i18n.resolvedLanguage}
            isLoading={false}
            height="calc(100dvh - 310px)"
            pageSize={20}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        </div>
      </div>
    </CustomScrollbarV2>
  );
}
